// PATH: src/components/settings/AgentCard.tsx
import * as React from "react";
import { Box, Card, Divider, Slider, Stack, Typography } from "@mui/material";
import { StatusPill } from "../ui/StatusPill";

export const temperatureHint = (t: number) =>
  t <= 0.15
    ? "Très stable : répète les mêmes tournures."
    : t <= 0.5
      ? "Équilibré : varie sans s'égarer."
      : t <= 0.8
        ? "Créatif : plus de variété, plus de risque de s'écarter de la consigne."
        : "Très libre : sorties imprévisibles.";

interface Props {
  icon: React.ReactNode;
  title: string;
  /** Une phrase : ce que fait cet agent pour l'élève. */
  role: string;
  /** Sous le libellé « Modèle » : ce que vaut le réglage, ce que le rôle demande au modèle. */
  modelHint: string;
  /** Fournisseur et modèle (ModelPicker). */
  modelControl: React.ReactNode;
  /** Absente = pas de température réglable ici (`temperatureNote` le dit). */
  temperature?: number;
  onTemperature?: (v: number) => void;
  /** Repère « défaut » sur la réglette. */
  defaultTemperature?: number;
  temperatureNote?: string;
  /** Réglage différent de ce qui est enregistré. */
  dirty?: boolean;
  footnote?: React.ReactNode;
  disabled?: boolean;
  /** Sections propres à l'agent, sous le modèle et la température. */
  children?: React.ReactNode;
}

/** En-tête de la carte : icône, titre, rôle, « Modifié ». */
const AgentCardHeader: React.FC<{ icon: React.ReactNode; title: string; role: string; dirty?: boolean }> = ({
  icon,
  title,
  role,
  dirty,
}) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ p: 2.5, pb: 2 }}>
    <Box
      sx={{
        display: "grid",
        placeItems: "center",
        width: 40,
        height: 40,
        borderRadius: 2.5,
        bgcolor: "primary.main",
        color: "primary.contrastText",
        flexShrink: 0,
        "& svg": { fontSize: 22 },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Typography variant="h5">{title}</Typography>
        {dirty && <StatusPill tone="warning">Modifié</StatusPill>}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
        {role}
      </Typography>
    </Box>
  </Stack>
);

/** Réglage d'un agent IA : fournisseur, modèle et température expliqués, puis ce qui lui est propre. */
export const AgentCard: React.FC<Props> = ({
  icon,
  title,
  role,
  modelHint,
  modelControl,
  temperature,
  onTemperature,
  defaultTemperature,
  temperatureNote,
  dirty,
  footnote,
  disabled,
  children,
}) => {
  return (
    <Card sx={{ display: "flex", flexDirection: "column" }}>
      <AgentCardHeader icon={icon} title={title} role={role} dirty={dirty} />
      <Divider />
      <Stack spacing={3} sx={{ p: 2.5, flex: 1 }}>
        <Box>
          <Typography variant="subtitle2">Fournisseur et modèle</Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
            {modelHint}
          </Typography>
          {modelControl}
        </Box>

        {temperature !== undefined && onTemperature ? (
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">Température</Typography>
              <Typography sx={{ fontWeight: 650, fontVariantNumeric: "tabular-nums" }}>{temperature.toFixed(2)}</Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, mb: 0.5, minHeight: 18 }}>
              {temperatureHint(temperature)}
            </Typography>
            <Box sx={{ px: 1 }}>
              <Slider
                value={temperature}
                min={0}
                max={1}
                step={0.05}
                disabled={disabled}
                onChange={(_, v) => onTemperature(v as number)}
                marks={[
                  { value: 0, label: "0" },
                  ...(defaultTemperature !== undefined ? [{ value: defaultTemperature, label: "défaut" }] : []),
                  { value: 1, label: "1" },
                ]}
                aria-label={`Température — ${title}`}
              />
            </Box>
          </Box>
        ) : (
          temperatureNote && (
            <Box>
              <Typography variant="subtitle2">Température</Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {temperatureNote}
              </Typography>
            </Box>
          )
        )}
        {children}
        {footnote && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: "auto !important" }}>
            {footnote}
          </Typography>
        )}
      </Stack>
    </Card>
  );
};

export default AgentCard;
